import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Play, CheckCircle, XCircle, Factory, Minus, AlertTriangle, Pencil, Undo2, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { trSayi, trPara } from '@/lib/sayi';
// Arama İKİ TARAFI da aynı fonksiyondan geçirir (ERP.md "Türkçe arama"):
// bir tarafı trNorm, öbürünü toLowerCase ile normallemek sessiz hatadır.
import { kelimeleriIceriyor } from '@/lib/arama';
import { useAuth } from '@/contexts/AuthContext';

// Sütun filtre kutularının ortak sınıfı — faturalar ekranındakiyle aynı
// (dar, tablo başlığına sığan). Tek yerde durması iki ekranın görsel olarak
// ayrışmasını önlüyor.
const KUTU = 'h-7 w-full px-1.5 rounded border bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e]';

// FİLTRELER İSTEMCİDE UYGULANIR — faturalar ekranının tersine.
//
// O ekranda süzme SUNUCUDA, çünkü liste SAYFALI: istemcide süzmek yalnızca
// açık sayfayı süzer ve "3 kayıt" yazarken öbür sayfalardaki eşleşmeler gizli
// kalır. Burada sayfalama YOK — iki uç da listenin tamamını döndürüyor
// (145 emir / 227 tüketim, sunucu tarafında 500 sınırı). Tamamı bellekte
// olduğu için süzme anlık ve dürüst; bekleme (debounce) da gerekmiyor.
// **500 sınırına yaklaşıldığında sayfalama + sunucu süzmesine geçilmeli**;
// tablo dibindeki uyarı o günü sessiz bırakmamak için var.
const AZAMI_KAYIT = 500;
const BOS_U_FILTRE = { tarih_bas: '', tarih_bit: '', urun: '', depo: '', durum: '' };
const BOS_T_FILTRE = { tarih_bas: '', tarih_bit: '', ad: '', kategori: '', depo: '', durum: '' };

// SUZGECLER VE ACIK SEKME SAYFA DEGISIMINDE KORUNUR (kullanici, 2026-09-28).
// Bir emri duzenleyip kaydeden kullanici listeye donuyor ama suzgecler
// sifirlandigi icin onaylayacagi kaydi bulmak uzere ayni aramayi bastan yapmak
// zorunda kaliyordu.
// **sessionStorage, localStorage DEGIL**: kalici olsaydi gunler sonra acilan
// ekranda gorunmez bir suzgec asili kalir ve "kayitlarim nerede" sorusuna yol
// acardi -- sayacin "suzulen / toplam" yazmasinin sebebi de ayni endiseydi.
// Tarayici sekmesi kapaninca sifirlanmasi dogru davranis.
const FILTRE_ANAHTARI = 'erp_uretim_suzgecleri';

const saklanan = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTRE_ANAHTARI)) || {}; }
  catch { return {}; }   // gizli kip / kota: hatirlamamak ekrani cokertmemeli
};

/** Yalnizca BILINEN alanlar geri yuklenir. Saklanan bicim degistiginde eski
 *  kayittan artik kullanilmayan bir alan suzgece sizip sessizce suzmeye devam
 *  ederdi; ekranda karsiligi olmayan bir filtre en kotu filtredir. */
const geriYukle = (bos, kayit) => Object.fromEntries(
  Object.keys(bos).map(k => [k, typeof kayit?.[k] === 'string' ? kayit[k] : bos[k]]));

/** Listede geçen tekil değerler — süzgeç seçenekleri VERİDEN üretilir, böylece
 *  kullanıcıya hiç kaydı olmayan bir depo/kategori gösterilmez. */
const tekiller = (liste, alan) =>
  [...new Set(liste.map(x => (x[alan] || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'tr'));

/** Tarih aralığı kontrolü — iş tarihi (gün) üzerinden, ISO metin karşılaştırması. */
const tarihUygun = (gun, bas, bit) =>
  (!bas || gun >= bas) && (!bit || gun <= bit);

// İş tarihi alanının adı iki listede FARKLI (ERP.md "Tarih"): üretimde
// `planned_date`, tüketimde `consumption_date`; ikisi de yoksa kayıt gününe
// düşülür — ekranda GÖSTERİLEN tarih neyse süzgeç de onu süzer.
export const uGun = (o) => String(o.planned_date || o.created_at || '').slice(0, 10);
export const tGun = (c) => String(c.consumption_date || c.created_at || '').slice(0, 10);

// Süzme bileşenin DIŞINDA: hem her çizimde yeniden yaratılmasın hem
// bileşeni ayağa kaldırmadan sınanabilsin.
export const suzUretim = (liste, f) => liste.filter(o =>
  tarihUygun(uGun(o), f.tarih_bas, f.tarih_bit)
  && (!f.urun || kelimeleriIceriyor(f.urun, o.product_name || ''))
  && (!f.depo || (o.warehouse_name || '') === f.depo)
  && (!f.durum || o.status === f.durum));

export const suzTuketim = (liste, f) => liste.filter(c =>
  tarihUygun(tGun(c), f.tarih_bas, f.tarih_bit)
  && (!f.ad || kelimeleriIceriyor(f.ad, c.name || ''))
  && (!f.kategori || (c.category_label || '') === f.kategori)
  && (!f.depo || (c.warehouse_name || '') === f.depo)
  && (!f.durum || c.status === f.durum));

// SİLİNEBİLİR DURUMLAR sunucudaki kümelerin aynası (`recipes.SILINEBILIR_EMIR`,
// `consumptions.DUZELTILEBILIR`). Asıl kural SUNUCUDA -- düğmeyi gizlemek yetki
// değildir; buradaki küme yalnızca kullanıcıya 400 aldıracak düğmeyi
// göstermemek için. Onaylanmış tüketim ve tamamlanmış üretim ASLA silinmez
// (kullanıcı, 2026-09-28).
const SILINEBILIR_EMIR = ['planned', 'in_progress', 'cancelled'];
const SILINEBILIR_TUKETIM = ['pending', 'rejected'];

const statusLabels = { planned: 'Planli', in_progress: 'Devam Ediyor', completed: 'Tamamlandi', cancelled: 'Iptal' };
const statusColors = {
  planned: 'bg-[#e8dfd0] text-[#3F3E39]', in_progress: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  completed: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', cancelled: 'bg-[#7a3535]/10 text-[#7a3535]'
};
const cStatusLabels = { pending: 'Beklemede', approved: 'Onaylandi', rejected: 'Reddedildi' };
const cStatusColors = {
  pending: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  approved: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  rejected: 'bg-[#7a3535]/10 text-[#7a3535]'
};

export default function ProductionOrders() {
  const [orders, setOrders] = useState([]);
  const [consumptions, setConsumptions] = useState([]);
  const [uFiltre, setUFiltre] = useState(() => geriYukle(BOS_U_FILTRE, saklanan().uretim));
  const [tFiltre, setTFiltre] = useState(() => geriYukle(BOS_T_FILTRE, saklanan().tuketim));
  const location = useLocation();
  // Acik gonderilen sekme (`location.state`) saklanandan ONCE gelir: acik bir
  // yonlendirme niyeti, hatirlanan tercihi ezmelidir.
  const [activeTab, setActiveTab] = useState(
    () => location.state?.tab || saklanan().sekme || 'production');
  const [completing, setCompleting] = useState(null);
  const [saving, setSaving] = useState(false);
  // Tamamlanmis emri geri alma: stok hareketleri iade edilir, emir yeniden acilir
  const [geriAl, setGeriAl] = useState(null);   // { order, uyari }
  // Duzenleme ve geri alma uclari `production_orders/edit` istiyor; yetkisi
  // olmayana dugmeyi gostermek 403'e goturen bir dugme demekti.
  const { hasPermission } = useAuth();
  const duzeltebilir = hasPermission('production_orders', 'edit');
  // TAMAMLA ve GERİ AL onaylayanın işi (`approve`): tamamlamak onaylamaktır
  // (kullanıcı, 2026-09-28: "Onaylamaya kimin yetkisi varsa o Tamamlar"),
  // geri almak onayı bozmaktır. `edit` yalnızca onaylanmamış kayda aittir.
  const onaylayabilir = hasPermission('production_orders', 'approve');
  // SİLME `delete` İSTER (sunucu `module_permission`: DELETE -> "delete").
  // Yetki matrisindeki "Sil" kutusu bir dönem hiçbir yerde kontrol edilmiyordu;
  // ekran da sunucuyla aynı ölçüyü kullanmalı (kullanıcı, 2026-09-28).
  const uretimSilebilir = hasPermission('production_orders', 'delete');
  const tuketimSilebilir = hasPermission('consumptions', 'delete');
  // Silme onayı: { tur: 'uretim' | 'tuketim', kayit }
  const [silinecek, setSilinecek] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    fetchConsumptions();
  }, []);

  // Ayrilma anini beklemek yerine DEGISTIKCE yazilir: unmount her zaman
  // calismaz (sekme kapatma, sayfa yenileme) ve yazma zaten ucuz.
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTRE_ANAHTARI, JSON.stringify(
        { uretim: uFiltre, tuketim: tFiltre, sekme: activeTab }));
    } catch { /* gizli kip / kota: suzgec hatirlanmaz, ekran calismaya devam eder */ }
  }, [uFiltre, tFiltre, activeTab]);

  const gorunenOrders = useMemo(() => suzUretim(orders, uFiltre), [orders, uFiltre]);
  const gorunenConsumptions = useMemo(
    () => suzTuketim(consumptions, tFiltre), [consumptions, tFiltre]);

  const uFiltreVar = Object.values(uFiltre).some(Boolean);
  const tFiltreVar = Object.values(tFiltre).some(Boolean);

  const fetchOrders = () => api.get('/production-orders').then(r => setOrders(r.data)).catch(() => {});
  const fetchConsumptions = () => api.get('/consumptions').then(r => setConsumptions(r.data)).catch(() => {});

  const startOrder = async (id) => {
    try {
      await api.patch(`/production-orders/${id}/start`);
      toast.success('Uretim baslatildi');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  // Tamamlama, gerceklesen miktarlar sorulmadan yapilamaz: planlanan ile
  // gerceklesen ayni olmak zorunda degil ve birim maliyet gerceklesen uretime
  // bolunuyor. Eskiden bos govde gonderiliyor, actual_quantity her zaman
  // planlanana esitleniyordu.
  const openComplete = (order) => {
    setCompleting({
      order,
      actual: String(order.planned_quantity ?? ''),
      ingredients: (order.ingredients_consumed || []).map(i => ({
        product_card_id: i.product_card_id,
        product_name: i.product_name || i.product_card_id,
        unit: i.unit,
        planned: i.planned_qty,
        actual: String(i.actual_qty ?? i.planned_qty ?? ''),
      })),
    });
  };

  const submitComplete = async () => {
    if (!completing) return;
    const uretilen = parseFloat(completing.actual);
    if (!(uretilen > 0)) { toast.error('Uretilen miktar sifirdan buyuk olmali'); return; }
    setSaving(true);
    try {
      const res = await api.patch(`/production-orders/${completing.order.id}/complete`, {
        actual_quantity: uretilen,
        ingredients: completing.ingredients
          .filter(i => i.actual !== '' && !isNaN(parseFloat(i.actual)))
          .map(i => ({ product_card_id: i.product_card_id, actual_qty: parseFloat(i.actual) })),
      });
      const birim = res.data?.unit_cost;
      toast.success(
        birim != null
          ? `Uretim tamamlandi — birim maliyet ${trPara(birim)} TL`
          : 'Uretim tamamlandi'
      );
      setCompleting(null);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setSaving(false);
  };

  // GERİ AL: emri düzeltmenin tek yolu. Tamamlanmış emir stok hareketi
  // doğurduğu için doğrudan düzenlenemiyor; önce hareketler iade edilir.
  const geriAlUygula = async (zorla) => {
    if (!geriAl) return;
    setSaving(true);
    try {
      const res = await api.patch(`/production-orders/${geriAl.order.id}/geri-al`,
        zorla ? { zorla: true } : {});
      toast.success(`Üretim geri alındı — ${res.data?.silinen_hareket ?? 0} stok hareketi iade edildi`);
      setGeriAl(null);
      fetchOrders();
    } catch (err) {
      const d = err.response?.data?.detail;
      // 409: stok eksiye düşecek. Kullanıcıya SORULUR, sessizce yapılmaz.
      if (err.response?.status === 409 && d?.mesaj) setGeriAl(g => ({ ...g, uyari: d.mesaj }));
      else toast.error(typeof d === 'string' ? d : 'Geri alınamadı');
    }
    setSaving(false);
  };

  const silUygula = async () => {
    if (!silinecek) return;
    const { tur, kayit } = silinecek;
    setSaving(true);
    try {
      await api.delete(tur === 'uretim' ? `/production-orders/${kayit.id}` : `/consumptions/${kayit.id}`);
      toast.success(tur === 'uretim' ? 'Üretim emri silindi' : 'Tüketim silindi');
      setSilinecek(null);
      if (tur === 'uretim') fetchOrders(); else fetchConsumptions();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Silinemedi');
      // 409: kayıt bu arada onaylanmış/tamamlanmış olabilir -- listeyi tazele
      // ki ekran artık geçerli olmayan bir düğme göstermeye devam etmesin.
      if (err.response?.status === 409) { fetchOrders(); fetchConsumptions(); }
    }
    setSaving(false);
  };

  const cancelOrder = async (id) => {
    try {
      await api.patch(`/production-orders/${id}/cancel`);
      toast.success('Uretim iptal edildi');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  return (
    <div data-testid="production-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Uretim ve Tuketim</h1>
          {/* Süzgeç varken HEM süzülen HEM toplam yazılır: yalnızca süzülen
              sayıyı göstermek "kayıtlarım nerede" sorusuna yol açıyordu. */}
          <p className="text-sm text-[#8a7e70] mt-1" data-testid="kayit-sayaci">
            {activeTab === 'production'
              ? (uFiltreVar
                  ? `${gorunenOrders.length} / ${orders.length} uretim emri`
                  : `${orders.length} uretim emri`)
              : (tFiltreVar
                  ? `${gorunenConsumptions.length} / ${consumptions.length} tuketim`
                  : `${consumptions.length} tuketim`)}
            {((activeTab === 'production' && uFiltreVar)
              || (activeTab === 'consumptions' && tFiltreVar)) && (
              <button type="button"
                onClick={() => activeTab === 'production'
                  ? setUFiltre(BOS_U_FILTRE) : setTFiltre(BOS_T_FILTRE)}
                className="ml-2 inline-flex items-center text-xs text-[#3d6e4e] hover:underline"
                data-testid="filtre-temizle">
                <X size={12} className="mr-0.5" /> filtreleri temizle
              </button>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'production' && (
            <Link to="/erp/production-orders/new">
              <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-production-btn">
                <Factory size={16} className="mr-2" /> Uretim
              </Button>
            </Link>
          )}
          {activeTab === 'consumptions' && (
            <Link to="/erp/consumptions/new">
              <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-consumption-btn">
                <Minus size={16} className="mr-2" /> Tuketim
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="production" data-testid="tab-production">
            <Factory size={14} className="mr-1.5" /> Uretim Emirleri
          </TabsTrigger>
          <TabsTrigger value="consumptions" data-testid="tab-consumptions">
            <Minus size={14} className="mr-1.5" /> Tuketimler
          </TabsTrigger>
        </TabsList>

        <TabsContent value="production">
          <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Uretim Tarihi</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Depo</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-center">Durum</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Maliyet</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islemler</TableHead>
                </TableRow>
                {/* SÜZGEÇ SATIRI — sütunun altında, gösterdiği veriyi süzer */}
                <TableRow className="bg-[#f1e9da] hover:bg-[#f1e9da]">
                  <TableHead className="py-1.5 px-2 align-top w-[130px]">
                    <div className="flex flex-col gap-1">
                      <input type="date" value={uFiltre.tarih_bas} title="Üretim tarihi — başlangıç"
                        onChange={e => setUFiltre(p => ({ ...p, tarih_bas: e.target.value }))}
                        className={`${KUTU} ${uFiltre.tarih_bas ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                        data-testid="uf-tarih-bas" />
                      <input type="date" value={uFiltre.tarih_bit} title="Üretim tarihi — bitiş"
                        onChange={e => setUFiltre(p => ({ ...p, tarih_bit: e.target.value }))}
                        className={`${KUTU} ${uFiltre.tarih_bit ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                        data-testid="uf-tarih-bit" />
                    </div>
                  </TableHead>
                  <TableHead className="py-1.5 px-2 align-top min-w-[150px]">
                    <input type="text" value={uFiltre.urun} placeholder="Ürün adı içerir"
                      onChange={e => setUFiltre(p => ({ ...p, urun: e.target.value }))}
                      className={`${KUTU} ${uFiltre.urun ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid="uf-urun" />
                  </TableHead>
                  <TableHead className="py-1.5 px-2 align-top w-[130px]">
                    <select value={uFiltre.depo}
                      onChange={e => setUFiltre(p => ({ ...p, depo: e.target.value }))}
                      className={`${KUTU} ${uFiltre.depo ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid="uf-depo">
                      <option value="">Tüm depolar</option>
                      {tekiller(orders, 'warehouse_name').map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </TableHead>
                  <TableHead className="py-1.5 px-2 align-top w-[120px]">
                    <select value={uFiltre.durum}
                      onChange={e => setUFiltre(p => ({ ...p, durum: e.target.value }))}
                      className={`${KUTU} ${uFiltre.durum ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid="uf-durum">
                      <option value="">Tüm durumlar</option>
                      {Object.entries(statusLabels).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </TableHead>
                  <TableHead colSpan={3} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {gorunenOrders.map((o) => (
                  <TableRow key={o.id} className="erp-table-row border-[#d0c4b0]">
                    {/* Uretimin GERCEKLESTIGI gun (`planned_date`) — stok hareketleri de ona
                        yaziliyor. Kayit gunu (`created_at`) gosterilirse gecmis tarihli
                        uretim listede bugun girilmis gibi gorunuyordu. */}
                    <TableCell className="text-sm text-[#3F3E39]">
                      {new Date(o.planned_date || o.created_at).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell className="font-medium text-[#1a1916]">
                      {o.product_name}
                      {/* Recetesiz emirde girdiler elle yazildi; listede
                          ayirt edilmezse "bu neden recetesinden farkli
                          tuketmis" sorusu cevapsiz kalir. */}
                      {!o.recipe_id && (
                        <span className="ml-2 align-middle rounded px-1.5 py-0.5 text-[10px] font-semibold
                                         uppercase bg-[#7a6a2e]/10 text-[#7a6a2e]">
                          Reçetesiz
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#3F3E39]">{o.warehouse_name}</TableCell>
                    <TableCell className="text-center">
                      <span className={`status-badge ${statusColors[o.status]}`}>{statusLabels[o.status]}</span>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-[#1a1916]">
                      {trSayi(o.actual_quantity ?? o.planned_quantity)} <span className="text-[#8a7e70] font-normal text-xs">{o.quantity_unit}</span>
                      {o.status === 'completed' && o.actual_quantity != null
                        && o.actual_quantity !== o.planned_quantity && (
                        <div className="text-xs font-normal text-[#7a6a2e]">
                          plan {trSayi(o.planned_quantity)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#3F3E39]">
                      {o.total_cost ? `${trPara(o.total_cost)} TL` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {o.status === 'planned' && (
                          <Button size="sm" variant="ghost" onClick={() => startOrder(o.id)}
                            className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10" data-testid={`start-order-${o.id}`}>
                            <Play size={14} className="mr-1" /> Baslat
                          </Button>
                        )}
                        {(o.status === 'planned' || o.status === 'in_progress') && (
                          <>
                            {duzeltebilir && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/erp/production-orders/${o.id}/duzenle`)}
                                className="text-[#3F3E39] hover:bg-[#e8dfd0]" data-testid={`edit-order-${o.id}`}>
                                <Pencil size={14} className="mr-1" /> Duzenle
                              </Button>
                            )}
                            {onaylayabilir && (
                              <Button size="sm" variant="ghost" onClick={() => openComplete(o)}
                                className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10" data-testid={`complete-order-${o.id}`}>
                                <CheckCircle size={14} className="mr-1" /> Tamamla
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => cancelOrder(o.id)}
                              className="text-[#7a3535] hover:bg-[#7a3535]/10" data-testid={`cancel-order-${o.id}`}>
                              <XCircle size={14} />
                            </Button>
                          </>
                        )}
                        {SILINEBILIR_EMIR.includes(o.status) && uretimSilebilir && (
                          <Button size="sm" variant="ghost" onClick={() => setSilinecek({ tur: 'uretim', kayit: o })}
                            className="text-[#7a3535] hover:bg-[#7a3535]/10" title="Sil"
                            data-testid={`delete-order-${o.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                        {/* Tamamlanmis emirde tek mudahale yolu: once geri al */}
                        {o.status === 'completed' && onaylayabilir && (
                          <Button size="sm" variant="ghost" onClick={() => setGeriAl({ order: o, uyari: null })}
                            className="text-[#7a6a2e] hover:bg-[#7a6a2e]/10" data-testid={`undo-order-${o.id}`}>
                            <Undo2 size={14} className="mr-1" /> Geri Al
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* "Hic kayit yok" ile "suzgec sonuc vermedi" AYRI mesajlar:
                    ayni metin kullaniciya verisini kaybettigini dusundurur. */}
                {gorunenOrders.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#8a7e70]">
                    {orders.length === 0
                      ? 'Emir bulunamadi'
                      : 'Suzgece uyan uretim emri yok - filtreleri gevsetin'}
                  </TableCell></TableRow>
                )}
                {orders.length >= AZAMI_KAYIT && (
                  <TableRow><TableCell colSpan={7} className="py-2 text-center text-[11px] text-[#7a6a2e] bg-[#7a6a2e]/5">
                    Sunucu en cok {AZAMI_KAYIT} kayit gonderiyor; liste bu sinira
                    ulasti. Suzgecler yalnizca gelen kayitlar uzerinde calisir -
                    sayfalama eklenmeli.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="consumptions">
          <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ad</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kategori</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Depo</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-center">Urun Sayisi</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-center">Porsiyon</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Maliyet</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-center">Durum</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islemler</TableHead>
                </TableRow>
                {/* SÜZGEÇ SATIRI */}
                <TableRow className="bg-[#f1e9da] hover:bg-[#f1e9da]">
                  <TableHead className="py-1.5 px-2 align-top w-[130px]">
                    <div className="flex flex-col gap-1">
                      <input type="date" value={tFiltre.tarih_bas} title="Tüketim tarihi — başlangıç"
                        onChange={e => setTFiltre(p => ({ ...p, tarih_bas: e.target.value }))}
                        className={`${KUTU} ${tFiltre.tarih_bas ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                        data-testid="tf-tarih-bas" />
                      <input type="date" value={tFiltre.tarih_bit} title="Tüketim tarihi — bitiş"
                        onChange={e => setTFiltre(p => ({ ...p, tarih_bit: e.target.value }))}
                        className={`${KUTU} ${tFiltre.tarih_bit ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                        data-testid="tf-tarih-bit" />
                    </div>
                  </TableHead>
                  <TableHead className="py-1.5 px-2 align-top min-w-[150px]">
                    <input type="text" value={tFiltre.ad} placeholder="Ad içerir"
                      onChange={e => setTFiltre(p => ({ ...p, ad: e.target.value }))}
                      className={`${KUTU} ${tFiltre.ad ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid="tf-ad" />
                  </TableHead>
                  <TableHead className="py-1.5 px-2 align-top w-[140px]">
                    <select value={tFiltre.kategori}
                      onChange={e => setTFiltre(p => ({ ...p, kategori: e.target.value }))}
                      className={`${KUTU} ${tFiltre.kategori ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid="tf-kategori">
                      <option value="">Tüm kategoriler</option>
                      {tekiller(consumptions, 'category_label').map(k => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </TableHead>
                  <TableHead className="py-1.5 px-2 align-top w-[130px]">
                    <select value={tFiltre.depo}
                      onChange={e => setTFiltre(p => ({ ...p, depo: e.target.value }))}
                      className={`${KUTU} ${tFiltre.depo ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid="tf-depo">
                      <option value="">Tüm depolar</option>
                      {tekiller(consumptions, 'warehouse_name').map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </TableHead>
                  <TableHead colSpan={3} />
                  <TableHead className="py-1.5 px-2 align-top w-[120px]">
                    <select value={tFiltre.durum}
                      onChange={e => setTFiltre(p => ({ ...p, durum: e.target.value }))}
                      className={`${KUTU} ${tFiltre.durum ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid="tf-durum">
                      <option value="">Tüm durumlar</option>
                      {Object.entries(cStatusLabels).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {gorunenConsumptions.map((c) => (
                  <TableRow key={c.id} className="erp-table-row border-[#d0c4b0]">
                    {/* Is tarihi gosterilir (kayit ani degil); eski kayitlarda
                        consumption_date yoksa created_at'e dusulur. */}
                    <TableCell className="text-sm text-[#3F3E39]">
                      {new Date(c.consumption_date || c.created_at).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell className="font-medium text-[#1a1916]">{c.name || '-'}</TableCell>
                    <TableCell className="text-sm text-[#3F3E39]">{c.category_label}</TableCell>
                    <TableCell className="text-sm text-[#3F3E39]">{c.warehouse_name}</TableCell>
                    <TableCell className="text-center text-sm text-[#3F3E39]">{c.item_count}</TableCell>
                    <TableCell className="text-center text-sm text-[#3F3E39]">
                      {c.portion_count != null ? c.portion_count : '-'}
                    </TableCell>
                    {/* Onaylanmisin maliyeti onay anında donduruldu; bekleyende
                        anlik fiyatla TAHMIN — kullanici hangisi oldugunu gorsun. */}
                    <TableCell className="text-right font-mono text-sm text-[#1a1916]">
                      {c.total_cost != null
                        ? `${c.total_cost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`
                        : '-'}
                      {c.maliyet_tahmini && c.total_cost != null && (
                        <span className="block text-[10px] text-[#8a7e70]">tahmini</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`status-badge ${cStatusColors[c.status]}`}>{cStatusLabels[c.status]}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Üretim emirlerindeki desenin aynısı: onaylanmamış
                            kayda satırdan doğrudan müdahale edilebilsin. */}
                        {(c.status === 'pending' || c.status === 'rejected') && (
                          <Button size="sm" variant="ghost"
                            onClick={() => navigate(`/erp/consumptions/${c.id}/edit`)}
                            className="text-[#3F3E39] hover:bg-[#e8dfd0]"
                            data-testid={`edit-consumption-${c.id}`}>
                            <Pencil size={14} className="mr-1" /> Duzenle
                          </Button>
                        )}
                        {SILINEBILIR_TUKETIM.includes(c.status) && tuketimSilebilir && (
                          <Button size="sm" variant="ghost" onClick={() => setSilinecek({ tur: 'tuketim', kayit: c })}
                            className="text-[#7a3535] hover:bg-[#7a3535]/10" title="Sil"
                            data-testid={`delete-consumption-${c.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/erp/consumptions/${c.id}`)}
                          className="text-[#3F3E39] hover:bg-[#e8dfd0]">
                          Detay
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {gorunenConsumptions.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#8a7e70]">
                    {consumptions.length === 0
                      ? 'Tuketim bulunamadi'
                      : 'Suzgece uyan tuketim yok - filtreleri gevsetin'}
                  </TableCell></TableRow>
                )}
                {consumptions.length >= AZAMI_KAYIT && (
                  <TableRow><TableCell colSpan={9} className="py-2 text-center text-[11px] text-[#7a6a2e] bg-[#7a6a2e]/5">
                    Sunucu en cok {AZAMI_KAYIT} kayit gonderiyor; liste bu sinira
                    ulasti. Suzgecler yalnizca gelen kayitlar uzerinde calisir -
                    sayfalama eklenmeli.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* SİLME PENCERESİ -- geri dönüşü yok, bu yüzden her zaman sorulur */}
      <Dialog open={!!silinecek} onOpenChange={(a) => !a && setSilinecek(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">
              {silinecek?.tur === 'uretim' ? 'Üretim Emrini Sil' : 'Tüketimi Sil'}
            </DialogTitle>
            <DialogDescription className="text-[#6b6255]">
              {silinecek?.tur === 'uretim'
                ? `${silinecek?.kayit?.product_name || '-'} — ${trSayi(silinecek?.kayit?.planned_quantity)} ${silinecek?.kayit?.quantity_unit || ''}`
                : (silinecek?.kayit?.name || '-')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-[#3F3E39]">
            <p>Kayıt <b>kalıcı olarak silinir</b> ve geri getirilemez.</p>
            <p className="text-[#6b6255]">
              {silinecek?.tur === 'uretim'
                ? 'Emir hiç tamamlanmadığı için stok hareketi yoktur; stok etkilenmez.'
                : 'Tüketim onaylanmadığı için stoktan hiçbir şey düşülmemiştir; stok etkilenmez.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSilinecek(null)} className="border-[#d0c4b0]">
              Vazgec
            </Button>
            <Button onClick={silUygula} disabled={saving}
              className="bg-[#7a3535] text-white hover:bg-[#5e2929]" data-testid="delete-confirm">
              <Trash2 size={14} className="mr-1.5" />
              {saving ? 'Siliniyor…' : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GERİ ALMA PENCERESİ */}
      <Dialog open={!!geriAl} onOpenChange={(a) => !a && setGeriAl(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Üretimi Geri Al</DialogTitle>
            <DialogDescription className="text-[#6b6255]">
              {geriAl?.order?.product_name} — {geriAl?.order?.actual_quantity ?? geriAl?.order?.planned_quantity}
              {' '}{geriAl?.order?.quantity_unit}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#3F3E39]">
            <p>Bu üretimin stok hareketleri <b>iade edilir</b>:</p>
            <ul className="list-disc pl-5 space-y-1 text-[#6b6255]">
              <li>Harcanan malzemeler depoya <b>geri eklenir</b>.</li>
              <li>Üretilen ürün depodan <b>düşülür</b>.</li>
              <li>Emir “Devam Ediyor” durumuna döner; düzeltip yeniden tamamlayabilirsiniz.</li>
            </ul>
            {geriAl?.uyari && (
              <div className="flex gap-2 rounded-md border border-[#7a3535]/30 bg-[#7a3535]/5 p-3">
                <AlertTriangle size={16} className="text-[#7a3535] shrink-0 mt-0.5" />
                <p className="text-[#7a3535]">{geriAl.uyari}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGeriAl(null)} className="border-[#d0c4b0]">
              Vazgec
            </Button>
            <Button onClick={() => geriAlUygula(!!geriAl?.uyari)} disabled={saving}
              className={geriAl?.uyari
                ? 'bg-[#7a3535] text-white hover:bg-[#5e2929]'
                : 'bg-[#7a6a2e] text-white hover:bg-[#5e5223]'}
              data-testid="undo-confirm">
              <Undo2 size={14} className="mr-1.5" />
              {saving ? 'İşleniyor…' : geriAl?.uyari ? 'Yine de Geri Al' : 'Geri Al'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Uretim tamamlama — gerceklesen cikti ve tuketim */}
      <Dialog open={!!completing} onOpenChange={(o) => { if (!o) setCompleting(null); }}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Uretimi Tamamla</DialogTitle>
            <DialogDescription className="text-[#8a7e70]">
              {completing?.order?.product_name} — gercekte ne kadar urun ciktigini yazin.
              Stok girisi ve birim maliyet bu miktara gore hesaplanir.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-5 py-1">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">
                Uretilen Miktar * <span className="text-[#8a7e70] font-normal">({completing?.order?.quantity_unit})</span>
              </Label>
              <div className="flex items-center gap-3">
                {/* step="any": olculen deger; sabit adim tarayicida "adim uyusmazligi" yapip formu sessizce engelliyor */}
                <Input type="number" step="any" autoFocus
                  value={completing?.actual ?? ''}
                  onChange={(e) => setCompleting(c => ({ ...c, actual: e.target.value }))}
                  className="bg-white border-[#d0c4b0] max-w-[200px]"
                  data-testid="actual-quantity-input" />
                <span className="text-sm text-[#8a7e70]">
                  Planlanan: {completing?.order?.planned_quantity} {completing?.order?.quantity_unit}
                </span>
              </div>
              {completing && parseFloat(completing.actual) > 0
                && parseFloat(completing.actual) !== completing.order.planned_quantity && (
                <div className="flex items-start gap-2 text-xs text-[#7a6a2e] bg-[#7a6a2e]/8 border border-[#7a6a2e]/25 rounded-md p-2">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>
                    Plandan sapma: {trSayi(parseFloat(completing.actual) - completing.order.planned_quantity)} {completing.order.quantity_unit}.
                    Birim maliyet gerceklesen miktara bolunecek.
                  </span>
                </div>
              )}
            </div>

            {completing?.ingredients?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Kullanilan Malzemeler</Label>
                <p className="text-xs text-[#8a7e70]">
                  Planlanandan farkli tukettiyseniz duzeltin; stok bu miktarlar uzerinden duser.
                </p>
                <div className="border border-[#d0c4b0] rounded-lg overflow-hidden bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                        <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Malzeme</TableHead>
                        <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Planlanan</TableHead>
                        <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right w-36">Gerceklesen</TableHead>
                        <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {completing.ingredients.map((ing, i) => (
                        <TableRow key={ing.product_card_id || i} className="border-[#d0c4b0]">
                          <TableCell className="text-sm text-[#1a1916]">{ing.product_name}</TableCell>
                          <TableCell className="text-right text-sm text-[#8a7e70]">{trSayi(ing.planned)}</TableCell>
                          <TableCell className="text-right">
                            <Input type="number" step="any" value={ing.actual}
                              onChange={(e) => setCompleting(c => ({
                                ...c,
                                ingredients: c.ingredients.map((x, j) =>
                                  j === i ? { ...x, actual: e.target.value } : x),
                              }))}
                              className="h-8 text-sm text-right bg-white border-[#d0c4b0]" />
                          </TableCell>
                          <TableCell className="text-sm text-[#8a7e70]">{ing.unit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="border-[#d0c4b0] text-[#3F3E39]"
              onClick={() => setCompleting(null)}>Vazgec</Button>
            <Button onClick={submitComplete} disabled={saving}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"
              data-testid="confirm-complete-btn">
              <CheckCircle size={14} className="mr-1.5" /> Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
